// Reads go straight to the server and fall back to the IndexedDB cache when offline.
// Mutations are written to the outbox (outbox.ts) first and replayed in order, so they work offline.
// Reads apply the still-queued outbox ops on top of the server data ("optimistic view"), so the UI
// shows local changes immediately and while offline.
import type {
  AppSettings,
  Clip,
  CreateClipFields,
  CreateDocumentRequest,
  DocType,
  DocumentFull,
  DocumentSummary,
  CreateProjectRequest,
  Features,
  LoginRequest,
  MeResponse,
  Minutes,
  NotesOrder,
  PolishResponse,
  SetupRequest,
  UpdateClipRequest,
  UpdateDocumentRequest,
  UpdateSegmentRequest,
  Project,
  UpdateProjectRequest,
  UpdateSettingsRequest,
  UsageStats,
} from '../../../shared/types';
import { isAndroidApp } from './android';
import { removeManualDrafts } from './drafts';
import { defaultTitle } from './format';
import { ApiClientError, authEpoch, bumpAuthEpoch, request, setUnauthorizedHandler } from './http';
import { cacheDelete, cacheEntries, cacheGet, cachePut, recordingIdOfBlob } from './localdb';
import {
  acceptedListOps,
  acceptedMark,
  acceptedOpsFor,
  clearAuthRequired,
  dropQueuedLogout,
  enqueue,
  flush,
  forgetAccepted,
  hasPendingWrites,
  listOps,
  logoutQueuedState,
  notifyDocumentChanged,
  notifySessionChanged,
  noteUnauthorized,
  opsForDocument,
  queueLogout,
  sendQueuedLogout,
  wasRefusedHere,
  whenWritesSettled,
  type EnqueueResult,
  type OutboxOp,
} from './outbox';
import {
  actingUserId,
  discardOwnWork,
  dropViewData,
  noteCachedUser,
  noteFeatures,
  noteSignedIn,
  noteSignedOut,
  onViewDataDropped,
} from './session';

/** Clip as shown in the UI: server data plus local, not yet uploaded state. */
export type ViewClip = Clip & {
  /** True while the clip only exists in the outbox (not yet accepted by the server). */
  localPending?: boolean;
};

/** Document as shown in the UI: server (or cached) data with queued outbox operations applied. */
export type ViewDocument = Omit<DocumentFull, 'clips'> & {
  clips: ViewClip[];
  /** True while the document itself is only queued for creation. */
  localPending?: boolean;
};

/** List entry; `localPending` marks documents that so far only exist in the outbox. */
export type ViewSummary = DocumentSummary & {
  localPending?: boolean;
};

export interface ReadOptions {
  /** Aborts the server request (the promise then rejects with an AbortError). */
  signal?: AbortSignal;
}

const enc = encodeURIComponent;
const PREVIEW_LENGTH = 200;
/** Server default (MAX_UPLOAD_MB=500), used while the real limit is unknown. */
const DEFAULT_MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

const DEFAULT_FEATURES: Features = {
  authRequired: false,
  authenticated: true,
  setupRequired: false,
  user: null,
  restricted: false,
  provider: '',
  diarization: false,
  ai: false,
  maxUploadBytes: DEFAULT_MAX_UPLOAD_BYTES,
};

// Results built from the offline cache are remembered here so hooks can flag them as stale
// without changing the return types.
const fromCache = new WeakSet<object>();

function markFromCache<T>(value: T): T {
  if (typeof value === 'object' && value !== null) fromCache.add(value);
  return value;
}

/**
 * Ops the server already accepted, laid over a stale offline copy so the change does not seem to
 * vanish (the copy predates it). Unlike queued ops they are not shown as pending.
 */
const acceptedOps = new WeakSet<OutboxOp>();

function markAccepted(ops: OutboxOp[]): OutboxOp[] {
  for (const op of ops) acceptedOps.add(op);
  return ops;
}

const isPendingOp = (op: OutboxOp) => !acceptedOps.has(op);

/** True when the value returned by a read function came from the offline cache. */
export function isFromCache(value: unknown): boolean {
  return typeof value === 'object' && value !== null && fromCache.has(value);
}

/** Network failure, timeout or server outage: the cache is a sensible fallback. */
function isUnreachable(err: unknown): err is ApiClientError {
  return err instanceof ApiClientError && err.transient;
}

const docKey = (id: string) => `doc:${id}`;

/** updatedAt of the copy last written per document, so polling does not rewrite unchanged data. */
const cachedVersions = new Map<string, string>();

function cacheDocument(doc: DocumentFull): void {
  if (cachedVersions.get(doc.id) === doc.updatedAt) return;
  cachedVersions.set(doc.id, doc.updatedAt);
  void cachePut(docKey(doc.id), doc);
}

function uncacheDocument(id: string): Promise<void> {
  cachedVersions.delete(id);
  return cacheDelete(docKey(id));
}

// ---------- auth ----------

/**
 * Features as cached on this device; `signedOut` marks a session that ended – by a logout here or
 * on the server – after which the app never opens as signed in offline (see getFeatures).
 */
type CachedFeatures = Features & { signedOut?: boolean };

/** Nobody is signed in: the login (or the first setup) is needed. */
const isSignedOutAnswer = (f: Features) => f.authRequired && !f.authenticated;

function signedOutFeatures(f: Features): Features {
  return { ...DEFAULT_FEATURES, ...f, authRequired: true, authenticated: false, user: null, restricted: false };
}

/** Offline the app opens signed out from now on (until a sign-in or a fresh answer says otherwise). */
async function markSignedOut(): Promise<void> {
  const cached = await cacheGet<CachedFeatures>('features');
  await cachePut('features', { ...signedOutFeatures({ ...DEFAULT_FEATURES, ...cached }), signedOut: true });
}

/**
 * The session ended without a logout on this device (revoked, expired, user disabled, password
 * changed elsewhere): the offline copies and recently accepted changes go. Unsent work, the list
 * of refused changes (the only copy of their text) and typed drafts stay for when the same user
 * signs in again; another user who signs in never sees them (session.adoptUser).
 */
async function sessionGone(): Promise<void> {
  await dropViewData({ failed: false });
  await markSignedOut();
}

// Any request answered 401 `unauthorized`: like a 401 during replay, the login is shown.
setUnauthorizedHandler(() => {
  noteUnauthorized();
  void sessionGone();
});

const authListeners = new Set<(me: MeResponse | null) => void>();

/**
 * Notified after a login / setup (with the new session's user) and after a logout or another
 * change of the account's state (null): the features must be re-read.
 */
export function subscribeAuth(listener: (me: MeResponse | null) => void): () => void {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

function emitAuthChange(me: MeResponse | null): void {
  for (const listener of [...authListeners]) listener(me);
}

/**
 * The signed-in user's account changed (new password, 2FA switched on, security settings): re-read
 * the features, which say whether the session is still restricted.
 */
export function authChanged(): void {
  emitAuthChange(null);
}

// Another user signed in on this device: the versions of the copies written before are moot.
onViewDataDropped(() => cachedVersions.clear());

// ---------- reads ----------

/**
 * GET /api/features. Offline: the last answer as cached on this device (signed in only when that
 * answer was, and no session ended since – see CachedFeatures), or a conservative default when
 * nothing is cached. A logout this device could not send yet is sent with it; while it cannot be,
 * the app counts as signed out. While the browser storage cannot tell whether a logout is queued,
 * a signed-in answer is returned as it is, but nothing is sent meanwhile (the session gate holds)
 * and the app shows its storage check instead of the content (session.adoptUser rejects then):
 * signing in again would not help.
 */
export async function getFeatures(): Promise<Features> {
  const epoch = authEpoch();
  // Read alongside the request: a browser storage that is slow to open must not hold up the
  // server's answer. (Until the server knows about a queued logout, the cookie may still open the
  // ended session: such an answer is overruled below.)
  const queuedAtStart = logoutQueuedState();
  let features: Features;
  try {
    features = await request<Features>('/api/features');
  } catch (err) {
    // The session ended (see the unauthorized handler): the login is shown.
    if (err instanceof ApiClientError && err.status === 401) return signedOutFeatures({ ...DEFAULT_FEATURES, ...(await cachedFeatures()) });
    if (!isUnreachable(err)) throw err;
    return offlineFeatures();
  }
  // Signed in meanwhile: this answer is older than the new session.
  if (epoch !== authEpoch()) return features;
  let queued = await queuedAtStart;
  // A logout in this tab while the request was under way (known in memory by now).
  if (queued === 'no') queued = await logoutQueuedState();
  if (queued === 'yes') {
    // Tell the server now (when it fails, the outbox keeps trying); signed out either way.
    await sendQueuedLogout();
    features = signedOutFeatures(features);
  } else if (queued === 'unknown' && features.authenticated && features.user) {
    // Storage has not answered yet: hold the queue until it does (the session is checked again then).
    noteFeatures(signedOutFeatures(features));
    return features;
  }
  if (isSignedOutAnswer(features)) {
    // A session that existed is gone: its offline copies go too.
    if (!(await cacheGet<CachedFeatures>('features'))?.signedOut) await dropViewData({ failed: false });
    void cachePut('features', { ...features, signedOut: true });
  } else {
    void cachePut('features', features);
  }
  noteFeatures(features);
  return features;
}

/** The cached features without the local marker. */
async function cachedFeatures(): Promise<Features | undefined> {
  const cached = await cacheGet<CachedFeatures>('features');
  if (!cached) return undefined;
  const { signedOut: _signedOut, ...rest } = cached;
  return rest;
}

async function offlineFeatures(): Promise<Features> {
  const cached = await cacheGet<CachedFeatures>('features');
  if (!cached) return markFromCache({ ...DEFAULT_FEATURES });
  const { signedOut, ...rest } = cached;
  if (signedOut || isSignedOutAnswer(rest) || (await logoutQueuedState()) !== 'no') {
    return markFromCache(signedOutFeatures(rest));
  }
  noteCachedUser(rest.user?.id ?? null);
  return markFromCache({ ...DEFAULT_FEATURES, ...rest });
}

function signedIn(me: MeResponse): void {
  // A new session: answers to requests sent before it, and a logout still queued, are about the old one.
  bumpAuthEpoch();
  void dropQueuedLogout();
  noteSignedIn(me);
  // Offline right after this, the app opens as this user (not as the login page of before).
  void cacheGet<CachedFeatures>('features').then((cached) => {
    const { signedOut: _signedOut, ...rest }: CachedFeatures = cached ?? DEFAULT_FEATURES;
    return cachePut('features', {
      ...DEFAULT_FEATURES,
      ...rest,
      authRequired: true,
      authenticated: true,
      setupRequired: false,
      user: me.user,
      restricted: me.restricted,
    });
  });
  // First the new user (the app must not show the previous user's page for a moment), then the
  // queue resumes – the session gate checks whose changes it holds.
  emitAuthChange(me);
  clearAuthRequired();
}

/**
 * POST /api/auth/login. Throws ApiClientError: 401 `code_required` (password correct, the 2FA code
 * is missing – send again with `code`), 401 `invalid_credentials` / `invalid_code`, 429
 * `too_many_attempts`.
 */
export async function login(req: LoginRequest): Promise<MeResponse> {
  const body: LoginRequest = { username: req.username.trim(), password: req.password };
  const code = req.code?.replace(/\s+/g, '');
  if (code) body.code = code;
  const me = await request<MeResponse>('/api/auth/login', { method: 'POST', body });
  signedIn(me);
  return me;
}

/** POST /api/auth/setup: the first administrator (first start only); signs in like login(). */
export async function setup(req: SetupRequest): Promise<MeResponse> {
  const me = await request<MeResponse>('/api/auth/setup', {
    method: 'POST',
    body: { ...req, username: req.username.trim(), displayName: req.displayName.trim() },
  });
  signedIn(me);
  return me;
}

export interface LogoutOptions {
  /** Also delete this user's unsent changes and recordings from the device (the user confirmed it). */
  discardUnsent?: boolean;
}

export interface LogoutResult {
  /** False when the server could not be told yet (offline): it is told as soon as it is reachable. */
  serverNotified: boolean;
  /** `discardUnsent` was asked for, but the unsent work could not be deleted (it is still there). */
  discardFailed: boolean;
  /** Recordings still running (in another tab) that `discardUnsent` did not delete. */
  keptRecordings: number;
}

/**
 * Logout. This device first, whatever the server answers (also offline): the session counts as
 * ended here at once, and the offline copies, refused changes and this user's typed drafts are
 * removed. Unsent changes and recordings are kept unless `discardUnsent` (they are sent when the
 * same user signs in again, see session.ts); a recording still running in another tab is kept
 * anyway. Then POST /api/auth/logout; when the server cannot be reached it is sent as soon as it
 * can, before anything else (outbox.queueLogout). Never rejects.
 */
export async function logout(opts: LogoutOptions = {}): Promise<LogoutResult> {
  const userId = actingUserId();
  noteSignedOut();
  // The Android app keeps its own session and opens a new web session for its WebView itself:
  // a logout queued there would end that new one.
  const inApp = isAndroidApp();
  if (!inApp) await queueLogout();
  await dropViewData({ failed: true });
  removeManualDrafts(userId);
  await markSignedOut();
  let discardFailed = false;
  let keptRecordings = 0;
  if (opts.discardUnsent) {
    try {
      keptRecordings = await discardOwnWork(userId);
    } catch (err) {
      console.warn('[api] unsent work could not be deleted', err);
      discardFailed = true;
    }
  }
  notifySessionChanged();
  let serverNotified = true;
  if (inApp) {
    serverNotified = await request<void>('/api/auth/logout', { method: 'POST' }).then(
      () => true,
      (err: unknown) => err instanceof ApiClientError && err.status === 401,
    );
  } else {
    serverNotified = await sendQueuedLogout();
    // Not reachable: the outbox tries again (with its backoff, and at once when back online).
    if (!serverNotified) flush();
  }
  emitAuthChange(null);
  return { serverNotified, discardFailed, keptRecordings };
}

interface ListParams {
  type?: DocType;
  q?: string;
  limit?: number;
  /** Only the documents of this project. */
  project?: string;
  /** Notes: the server's order (default: the signed-in user's preference). */
  order?: NotesOrder;
}

function normalizeListParams(params: ListParams | undefined): ListParams {
  const out: ListParams = {};
  if (params?.type) out.type = params.type;
  if (params?.project) out.project = params.project;
  if (params?.order) out.order = params.order;
  const q = params?.q?.trim();
  if (q) out.q = q;
  if (params?.limit !== undefined && params.limit > 0) out.limit = Math.floor(params.limit);
  return out;
}

const listKey = (p: ListParams) =>
  `list:${JSON.stringify({
    type: p.type ?? null,
    q: p.q ?? null,
    limit: p.limit ?? null,
    ...(p.project ? { project: p.project } : {}),
    ...(p.order ? { order: p.order } : {}),
  })}`;

/**
 * GET /api/documents. Offline -> cached list (filtered client-side). Pending local creations are
 * merged in (on top, marked with `localPending`, see ViewSummary).
 */
export async function listDocuments(params?: ListParams, opts: ReadOptions = {}): Promise<DocumentSummary[]> {
  const p = normalizeListParams(params);
  // Snapshot the queue before the request: an op accepted meanwhile is then in both views
  // (de-duplicated below) instead of in neither.
  const opsPromise = listOps().catch(() => [] as OutboxOp[]);
  const mark = acceptedMark();
  let list: DocumentSummary[];
  let stale = false;
  try {
    const qs = new URLSearchParams();
    if (p.type) qs.set('type', p.type);
    if (p.project) qs.set('project', p.project);
    if (p.order) qs.set('order', p.order);
    if (p.q) qs.set('q', p.q);
    if (p.limit) qs.set('limit', String(p.limit));
    const query = qs.toString();
    list = await request<DocumentSummary[]>(`/api/documents${query ? `?${query}` : ''}`, { signal: opts.signal });
    forgetAccepted(mark);
    // Search results are not cached: offline searches filter the cached lists instead.
    if (!p.q) void cachePut(listKey(p), list);
  } catch (err) {
    if (!isUnreachable(err)) throw err;
    list = await cachedList(p);
    stale = true;
  }
  const queued = await opsPromise;
  const merged = mergePendingIntoList(list, stale ? [...markAccepted(acceptedListOps()), ...queued] : queued, p);
  return stale ? markFromCache(merged) : merged;
}

/** Offline list: the exact cached query if available, else everything cached, filtered here. */
async function cachedList(p: ListParams): Promise<DocumentSummary[]> {
  if (!p.q) {
    const exact = await cacheGet<DocumentSummary[]>(listKey(p));
    if (exact) return exact;
  }
  const byId = new Map<string, DocumentSummary>();
  const consider = (s: DocumentSummary) => {
    const known = byId.get(s.id);
    if (!known || known.updatedAt < s.updatedAt) byId.set(s.id, s);
  };
  const lists = await cacheEntries<DocumentSummary[]>('list:');
  for (const entry of lists) {
    if (Array.isArray(entry.value)) entry.value.forEach(consider);
  }
  if (lists.length === 0) {
    // No list was ever loaded on this device: use the documents that were opened. (Full documents
    // are large, so they are only scanned in this case.)
    for (const entry of await cacheEntries<DocumentFull>('doc:')) {
      if (entry.value && typeof entry.value === 'object') consider(toSummary(entry.value));
    }
  }
  const filtered = [...byId.values()].filter((s) => matchesList(s, p));
  sortSummaries(filtered, p.type);
  return p.limit ? filtered.slice(0, p.limit) : filtered;
}

function matchesList(s: DocumentSummary, p: ListParams): boolean {
  if (p.type && s.type !== p.type) return false;
  if (p.project && s.projectId !== p.project) return false;
  if (p.q) {
    const needle = p.q.toLocaleLowerCase('sl');
    return `${s.title}\n${s.preview}`.toLocaleLowerCase('sl').includes(needle);
  }
  return true;
}

/** Server order: createdAt desc; notes: open first, then by due date (none last), then newest. */
function sortSummaries(list: DocumentSummary[], type: DocType | undefined): void {
  if (type === 'note') {
    list.sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      if (a.dueAt !== b.dueAt) {
        if (!a.dueAt) return 1;
        if (!b.dueAt) return -1;
        return a.dueAt < b.dueAt ? -1 : 1;
      }
      return b.createdAt.localeCompare(a.createdAt);
    });
  } else {
    const doneNote = (s: DocumentSummary) => (s.type === 'note' && s.done ? 1 : 0);
    list.sort((a, b) => doneNote(a) - doneNote(b) || b.createdAt.localeCompare(a.createdAt));
  }
}

function toSummary(doc: DocumentSummary): DocumentSummary {
  return {
    id: doc.id,
    type: doc.type,
    title: doc.title,
    createdAt: doc.createdAt,
    updatedAt: doc.updatedAt,
    preview: doc.preview,
    clipCount: doc.clipCount,
    pendingCount: doc.pendingCount,
    errorCount: doc.errorCount,
    durationMs: doc.durationMs,
    dueAt: doc.dueAt,
    done: doc.done,
    ...(doc.ownerId !== undefined ? { ownerId: doc.ownerId } : {}),
    projectId: doc.projectId ?? null,
    projectName: doc.projectName ?? null,
    sortOrder: doc.sortOrder ?? null,
  };
}

function groupByDocument(ops: readonly OutboxOp[]): Map<string, OutboxOp[]> {
  const byDoc = new Map<string, OutboxOp[]>();
  for (const op of ops) {
    if (!op.docId) continue;
    const list = byDoc.get(op.docId);
    if (list) list.push(op);
    else byDoc.set(op.docId, [op]);
  }
  return byDoc;
}

/** Server list + pending creations (on top) − pending deletions + pending title/done/dueAt changes. */
function mergePendingIntoList(list: DocumentSummary[], ops: readonly OutboxOp[], p: ListParams): DocumentSummary[] {
  const byDoc = groupByDocument(ops);
  if (byDoc.size === 0) return list;
  const known = new Set(list.map((s) => s.id));

  const created: ViewSummary[] = [];
  for (const [docId, docOps] of byDoc) {
    if (known.has(docId) || !docOps.some((o) => o.kind === 'createDocument')) continue;
    const view = applyOps(null, docOps, docId);
    if (!view || !matchesList(view, p)) continue;
    const pending = docOps.some((o) => o.kind === 'createDocument' && isPendingOp(o));
    created.push(pending ? { ...toSummary(view), localPending: true } : toSummary(view));
  }
  created.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const existing: DocumentSummary[] = [];
  for (const summary of list) {
    const docOps = byDoc.get(summary.id);
    if (!docOps) {
      existing.push(summary);
      continue;
    }
    const updated = applySummaryOps(summary, docOps);
    if (updated) existing.push(updated);
  }

  let merged = [...created, ...existing];
  // Done/due changes move notes around; keep the server's note order.
  if (p.type === 'note') sortSummaries(merged, 'note');
  if (p.limit) merged = merged.slice(0, p.limit);
  return merged;
}

function applySummaryOps(summary: DocumentSummary, ops: readonly OutboxOp[]): DocumentSummary | null {
  let s: DocumentSummary | null = { ...summary };
  for (const op of ops) {
    if (op.kind === 'deleteDocument') s = null;
    else if (op.kind === 'updateDocument' && s) {
      const body = (op.body ?? {}) as UpdateDocumentRequest;
      if (body.title !== undefined) s.title = body.title.trim() || defaultTitle(s.type, new Date(s.createdAt));
      if (body.done !== undefined) s.done = body.done;
      if (body.dueAt !== undefined) s.dueAt = body.dueAt;
      if (body.projectId !== undefined) {
        s.projectId = body.projectId;
        s.projectName = projectNameOf(body.projectId);
      }
      if (body.sortOrder !== undefined) s.sortOrder = body.sortOrder;
      if (op.createdAt > s.updatedAt) s.updatedAt = op.createdAt;
    }
  }
  return s;
}

/**
 * GET /api/documents/:id with pending outbox operations applied on top (optimistic view).
 * Offline -> cached copy (+ pending ops). Returns null when the document does not exist anywhere
 * (not on the server, not in cache, not pending creation).
 * Throws the network error when offline and nothing is known locally (existence is unknown).
 */
export async function getDocument(id: string, opts: ReadOptions = {}): Promise<ViewDocument | null> {
  // Read the queue first (see listDocuments): an op the server accepts during the request then
  // shows up in the server data, the queue, or both – never in neither.
  const ops = await opsForDocument(id).catch(() => [] as OutboxOp[]);
  const pendingCreation = ops.some((o) => o.kind === 'createDocument');
  // A new document of this device whose creation was never sent: the server cannot know it yet
  // (the GET would only 404). Not for a creation that may exist already (see createDocument).
  if (ops.some((o) => o.kind === 'createDocument' && o.newDocument === true && o.attempts === 0)) {
    return withSource(applyOps(null, ops, id), { base: null, stale: false, ops });
  }
  const mark = acceptedMark();
  let base: DocumentFull | null = null;
  try {
    base = await request<DocumentFull>(`/api/documents/${enc(id)}`, { signal: opts.signal });
    forgetAccepted(mark, id);
    cacheDocument(base);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) {
      void uncacheDocument(id);
      forgetAccepted(mark, id);
      if (!pendingCreation) return null;
    } else if (isUnreachable(err)) {
      base = (await cacheGet<DocumentFull>(docKey(id))) ?? null;
      // The cached copy may predate changes the server accepted meanwhile: lay them over it.
      const accepted = markAccepted(acceptedOpsFor(id));
      const createdHere = accepted.some((o) => o.kind === 'createDocument');
      if (!base && !pendingCreation && !createdHere) throw err;
      return withSource(applyOps(base, [...accepted, ...ops], id), { base, stale: true, ops });
    } else {
      throw err;
    }
  }
  return withSource(applyOps(base, ops, id), { base, stale: false, ops });
}

// ---------- rebuilding a view without a request ----------

/** The server (or cached) copy a view was built from, so it can be rebuilt with newer queued ops. */
interface ViewSource {
  base: DocumentFull | null;
  /** The copy came from the offline cache. */
  stale: boolean;
  /** The queued ops laid over `base` (with their seq), in queue order. */
  ops: readonly OutboxOp[];
}

const viewSources = new WeakMap<object, ViewSource>();

function withSource(view: ViewDocument | null, source: ViewSource): ViewDocument | null {
  if (!view) return view;
  viewSources.set(view, source);
  return source.stale ? markFromCache(view) : view;
}

/**
 * `view` (from getDocument) rebuilt from the copy it was made of and `queued` – the document's
 * queued ops now (opsForDocument) – without a request, so a change shows at once while the refetch
 * is still running. Ops the server accepted after that copy was read are laid over it as well
 * (they are no longer queued, but not in the copy either). So is an op the view was built with
 * that has left the queue without this tab seeing it accepted or refused: another tab of the app
 * may have sent it (the queue is shared), and the copy predates it. Null when the document is gone
 * locally (queued deletion); undefined when `view` was not returned by getDocument.
 */
export function rebuildDocument(view: ViewDocument, queued: readonly OutboxOp[]): ViewDocument | null | undefined {
  const source = viewSources.get(view);
  if (!source) return undefined;
  const accepted = markAccepted(acceptedOpsFor(view.id));
  const known = new Set<number | undefined>([...queued, ...accepted].map((o) => o.seq));
  const left = source.ops.filter((o) => o.seq !== undefined && !known.has(o.seq) && !wasRefusedHere(o.seq));
  // Kept for the next rebuild as well, until a read replaces the copy.
  const ops = [...left, ...queued];
  return withSource(applyOps(source.base, [...left, ...accepted, ...queued], view.id), { ...source, ops });
}

/** How long getLatestDocument waits for the server's copy before it uses the one on this device. */
const LATEST_READ_TIMEOUT_MS = 5_000;

/**
 * `current` with every edit started so far included – for actions that read the document right
 * after a tap (export, copy), when a text field the same tap blurred is only now being saved.
 * With `network`, the server's copy is read (for at most a few seconds); otherwise, or when that
 * fails, `current` is rebuilt from the ops queued on this device. Without `network` and with
 * nothing being saved, `current` is returned right away (copying to the clipboard needs the tap's
 * user activation, which Safari drops while waiting for storage or the network).
 */
export async function getLatestDocument(current: ViewDocument, opts: { network?: boolean } = {}): Promise<ViewDocument> {
  const saving = hasPendingWrites();
  await whenWritesSettled();
  if (opts.network) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), LATEST_READ_TIMEOUT_MS);
    try {
      const fresh = await getDocument(current.id, { signal: ctrl.signal }).catch(() => null);
      if (fresh) return fresh;
    } finally {
      clearTimeout(timer);
    }
  } else if (!saving) {
    return current;
  }
  const queued = await opsForDocument(current.id).catch(() => null);
  return (queued && rebuildDocument(current, queued)) || current;
}

const DEFAULT_SETTINGS: AppSettings = { vocabulary: [], trimSilence: true };

export async function getSettings(): Promise<AppSettings> {
  const opsPromise = listOps().catch(() => [] as OutboxOp[]);
  let settings: AppSettings;
  let stale = false;
  try {
    settings = await request<AppSettings>('/api/settings');
    void cachePut('settings', settings);
  } catch (err) {
    if (!isUnreachable(err)) throw err;
    settings = { ...DEFAULT_SETTINGS, ...(await cacheGet<Partial<AppSettings>>('settings')) };
    stale = true;
  }
  // Queued saves carry only the fields they change.
  for (const op of await opsPromise) {
    if (op.kind === 'saveSettings') settings = { ...settings, ...(op.body as UpdateSettingsRequest) };
  }
  return stale ? markFromCache(settings) : settings;
}

function normalizeSettings(settings: Pick<AppSettings, 'vocabulary'>): Pick<AppSettings, 'vocabulary'> {
  const seen = new Set<string>();
  const vocabulary: string[] = [];
  for (const raw of settings.vocabulary) {
    const term = raw.trim();
    const key = term.toLocaleLowerCase('sl');
    if (!term || seen.has(key)) continue;
    seen.add(key);
    vocabulary.push(term);
  }
  return { ...settings, vocabulary };
}

/**
 * Saves the vocabulary. Queued like every mutation (only the vocabulary is sent, the other settings
 * keep their value); resolves with the (normalised) vocabulary that will be stored.
 */
export async function saveSettings(settings: Pick<AppSettings, 'vocabulary'>): Promise<Pick<AppSettings, 'vocabulary'>> {
  const normalized = normalizeSettings(settings);
  await queue({ kind: 'saveSettings', docId: null, method: 'PUT', url: '/api/settings', body: normalized });
  // Only the vocabulary is read from this copy offline (getSettings fills in the rest).
  void cachePut('settings', normalized);
  return normalized;
}

/**
 * Switches silence trimming before transcription on or off. Online only (throws ApiClientError):
 * it changes what the firm pays for, so the switch shows the server's state, not a queued wish.
 */
export async function saveTrimSilence(on: boolean): Promise<AppSettings> {
  const body: UpdateSettingsRequest = { trimSilence: on };
  return request<AppSettings>('/api/settings', { method: 'PUT', body });
}

/** GET /api/settings from the server only (no cache fallback, no queued changes); throws ApiClientError. */
export function getServerSettings(): Promise<AppSettings> {
  return request<AppSettings>('/api/settings');
}

/** GET /api/usage: recorded vs. sent audio (online only). */
export function getUsage(): Promise<UsageStats> {
  return request<UsageStats>('/api/usage');
}

// ---------- projects ----------

/** Names of the projects as last read, for the optimistic view of queued project changes. */
const projectNames = new Map<string, string>();

function projectNameOf(id: string | null): string | null {
  return id === null ? null : (projectNames.get(id) ?? null);
}

function rememberProjects(list: readonly Project[]): void {
  projectNames.clear();
  for (const p of list) projectNames.set(p.id, p.name);
}

/**
 * GET /api/projects. Offline: the list as cached on this device (marked, see isFromCache). A server
 * without the route (older version) has no projects.
 */
export async function getProjects(opts: ReadOptions = {}): Promise<Project[]> {
  let list: Project[];
  let stale = false;
  try {
    list = await request<Project[]>('/api/projects', { signal: opts.signal });
    void cachePut('projects', list);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) {
      list = [];
    } else {
      if (!isUnreachable(err)) throw err;
      list = (await cacheGet<Project[]>('projects')) ?? [];
      stale = true;
    }
  }
  rememberProjects(list);
  return stale ? markFromCache(list) : list;
}

/** The project list is changed online only (it is shared by the firm and a conflict needs an answer at once). */
export async function createProject(id: string, req: CreateProjectRequest): Promise<Project> {
  const project = await request<Project>(`/api/projects/${enc(id)}`, { method: 'PUT', body: req });
  projectNames.set(project.id, project.name);
  return project;
}

export async function updateProject(id: string, req: UpdateProjectRequest): Promise<Project> {
  const project = await request<Project>(`/api/projects/${enc(id)}`, { method: 'PATCH', body: req });
  projectNames.set(project.id, project.name);
  return project;
}

export async function deleteProject(id: string): Promise<void> {
  await request<void>(`/api/projects/${enc(id)}`, { method: 'DELETE' });
  projectNames.delete(id);
}

/** POST /api/documents/:id/minutes (online only; throws ApiClientError). */
export async function generateMinutes(docId: string): Promise<Minutes> {
  // Summarising a long meeting can take a while.
  const minutes = await request<Minutes>(`/api/documents/${enc(docId)}/minutes`, { method: 'POST', timeoutMs: 180_000 });
  // The server stored the minutes on the document: make sure the next read refreshes the cache.
  cachedVersions.delete(docId);
  notifyDocumentChanged(docId);
  return minutes;
}

/** POST /api/clips/:id/polish (online only). Returns the suggested text; nothing is saved. */
export async function polishClip(clipId: string): Promise<string> {
  const res = await request<PolishResponse>(`/api/clips/${enc(clipId)}/polish`, { method: 'POST', timeoutMs: 120_000 });
  return res.text;
}

/** URL for <audio src>. For clips still waiting in the outbox use `localAudioUrl` from outbox.ts. */
export function audioUrl(clipId: string): string {
  return `/api/clips/${encodeURIComponent(clipId)}/audio`;
}

// ---------- optimistic view ----------

const sortClips = (clips: ViewClip[]) =>
  clips.sort((a, b) => a.seq - b.seq || a.createdAt.localeCompare(b.createdAt));

function nextSeq(doc: ViewDocument): number {
  return doc.clips.reduce((max, c) => Math.max(max, c.seq), 0) + 1;
}

function synthesizeDocument(id: string, op: OutboxOp): ViewDocument {
  const req = (op.body ?? {}) as CreateDocumentRequest;
  const createdAt = req.createdAt ?? op.createdAt;
  return {
    id,
    type: req.type,
    title: req.title?.trim() || defaultTitle(req.type, new Date(createdAt)),
    createdAt,
    updatedAt: createdAt,
    preview: '',
    clipCount: 0,
    pendingCount: 0,
    errorCount: 0,
    durationMs: 0,
    dueAt: req.dueAt ?? null,
    done: false,
    projectId: req.projectId ?? null,
    projectName: projectNameOf(req.projectId ?? null),
    sortOrder: null,
    meta: { ...req.meta },
    speakers: [],
    minutes: null,
    clips: [],
    ...pendingFlag(op),
  };
}

/** `localPending` for what an op adds to the view: only while the op is still queued. */
function pendingFlag(op: OutboxOp): { localPending?: true } {
  return isPendingOp(op) ? { localPending: true } : {};
}

function findClip(doc: ViewDocument, op: OutboxOp): ViewClip | undefined {
  if (op.clipId) return doc.clips.find((c) => c.id === op.clipId);
  if (op.segmentId) return doc.clips.find((c) => c.segments.some((s) => s.id === op.segmentId));
  return undefined;
}

/** Applies one clip-level op. Returns true when clips changed (summary fields need recomputing). */
function applyClipOp(doc: ViewDocument, op: OutboxOp): boolean {
  switch (op.kind) {
    case 'createAudioClip': {
      if (!op.clipId || doc.clips.some((c) => c.id === op.clipId)) return false;
      const f = (op.body ?? {}) as CreateClipFields;
      doc.clips.push({
        id: op.clipId,
        documentId: doc.id,
        seq: f.seq ?? nextSeq(doc),
        createdAt: f.createdAt ?? op.createdAt,
        updatedAt: op.createdAt,
        durationMs: f.durationMs ?? null,
        mimeType: op.blobMimeType || op.blob?.type || null,
        hasAudio: true,
        status: 'queued',
        error: null,
        diarized: f.diarize ?? doc.type === 'meeting',
        text: '',
        segments: [],
        provider: null,
        edited: false,
        ...pendingFlag(op),
      });
      return true;
    }
    case 'createTextClip': {
      if (!op.clipId || doc.clips.some((c) => c.id === op.clipId)) return false;
      const f = (op.body ?? {}) as CreateClipFields;
      doc.clips.push({
        id: op.clipId,
        documentId: doc.id,
        seq: f.seq ?? nextSeq(doc),
        createdAt: f.createdAt ?? op.createdAt,
        updatedAt: op.createdAt,
        durationMs: null,
        mimeType: null,
        hasAudio: false,
        status: 'done',
        error: null,
        diarized: false,
        text: f.text ?? '',
        segments: [],
        provider: null,
        edited: false,
        ...pendingFlag(op),
      });
      return true;
    }
    case 'updateClip': {
      const clip = findClip(doc, op);
      if (!clip) return false;
      const body = (op.body ?? {}) as UpdateClipRequest;
      if (body.text !== undefined) {
        clip.text = body.text;
        clip.edited = true;
        if (clip.diarized) {
          // Mirrors the server: a hand-edited diarized clip becomes one unlabeled segment.
          clip.segments = [
            { id: `${clip.id}-edited`, speaker: null, startMs: 0, endMs: clip.durationMs ?? 0, text: body.text },
          ];
          clip.diarized = false;
        }
      }
      if (body.seq !== undefined) clip.seq = body.seq;
      clip.updatedAt = op.createdAt;
      return true;
    }
    case 'deleteClip': {
      const before = doc.clips.length;
      doc.clips = doc.clips.filter((c) => c.id !== op.clipId);
      return doc.clips.length !== before;
    }
    case 'updateSegment': {
      const clip = findClip(doc, op);
      const segment = clip?.segments.find((s) => s.id === op.segmentId);
      if (!clip || !segment) return false;
      const body = (op.body ?? {}) as UpdateSegmentRequest;
      if (body.text !== undefined) segment.text = body.text;
      if (body.speaker !== undefined) segment.speaker = body.speaker;
      clip.text = clip.segments.map((s) => s.text).join('\n');
      clip.edited = true;
      clip.updatedAt = op.createdAt;
      return true;
    }
    case 'retranscribeClip': {
      const clip = findClip(doc, op);
      if (!clip || !clip.hasAudio) return false;
      clip.status = 'queued';
      clip.error = null;
      clip.updatedAt = op.createdAt;
      return true;
    }
    default:
      return false;
  }
}

function applyDocumentUpdate(doc: ViewDocument, body: UpdateDocumentRequest): void {
  if (body.title !== undefined) doc.title = body.title.trim() || defaultTitle(doc.type, new Date(doc.createdAt));
  if (body.meta !== undefined) doc.meta = { ...doc.meta, ...body.meta };
  if (body.dueAt !== undefined) doc.dueAt = body.dueAt;
  if (body.done !== undefined) doc.done = body.done;
  if (body.speakers !== undefined) doc.speakers = body.speakers.map((s) => ({ ...s }));
  if (body.projectId !== undefined) {
    doc.projectId = body.projectId;
    doc.projectName = projectNameOf(body.projectId);
  }
  if (body.sortOrder !== undefined) doc.sortOrder = body.sortOrder;
}

function recomputeSummary(doc: ViewDocument): void {
  sortClips(doc.clips);
  doc.clipCount = doc.clips.length;
  doc.pendingCount = doc.clips.filter((c) => c.status === 'queued' || c.status === 'processing').length;
  doc.errorCount = doc.clips.filter((c) => c.status === 'error').length;
  doc.durationMs = doc.clips.reduce((sum, c) => sum + (c.durationMs ?? 0), 0);
  doc.preview = doc.clips
    .map((c) => c.text.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .slice(0, PREVIEW_LENGTH);
}

/** Server/cached document + this document's queued ops, in queue order. */
export function applyOps(base: DocumentFull | null, ops: readonly OutboxOp[], id: string): ViewDocument | null {
  const relevant = ops.filter((o) => o.docId === id);
  if (relevant.length === 0) return base;
  // Never mutate the caller's object (it may be a shared cache entry).
  let doc: ViewDocument | null = base ? structuredClone(base) : null;
  let clipsChanged = false;
  for (const op of relevant) {
    switch (op.kind) {
      case 'createDocument':
        if (!doc) {
          doc = synthesizeDocument(id, op);
          clipsChanged = true;
        }
        break;
      case 'deleteDocument':
        doc = null;
        break;
      case 'updateDocument':
        if (doc) applyDocumentUpdate(doc, (op.body ?? {}) as UpdateDocumentRequest);
        break;
      default:
        if (doc && applyClipOp(doc, op)) clipsChanged = true;
    }
    if (doc && op.createdAt > doc.updatedAt) doc.updatedAt = op.createdAt;
  }
  if (!doc) return null;
  if (clipsChanged) recomputeSummary(doc);
  return doc;
}

// ---------- mutations (queued in the outbox, resolve once persisted locally) ----------

/** Queue a mutation in the name of the user this tab works for (see session.actingUserId). */
function queue(input: Omit<OutboxOp, 'seq' | 'attempts' | 'createdAt' | 'userId'>): Promise<EnqueueResult> {
  const userId = actingUserId();
  return enqueue(userId ? { ...input, userId } : input);
}

/**
 * `isNew`: `id` was just generated for a new document (newId()), so reads need not ask the server
 * for it until the creation has been sent. Leave it unset when the document may exist already
 * (e.g. a recovered recording's document is created again, which is idempotent).
 */
export async function createDocument(id: string, req: CreateDocumentRequest, opts: { isNew?: boolean } = {}): Promise<void> {
  // Fix the creation time now: offline, the server would otherwise date it at upload time.
  const body: CreateDocumentRequest = { ...req, createdAt: req.createdAt ?? new Date().toISOString() };
  await queue({
    kind: 'createDocument',
    docId: id,
    method: 'PUT',
    url: `/api/documents/${enc(id)}`,
    body,
    ...(opts.isNew ? { newDocument: true } : {}),
  });
}

export async function updateDocument(id: string, req: UpdateDocumentRequest): Promise<void> {
  await queue({ kind: 'updateDocument', docId: id, method: 'PATCH', url: `/api/documents/${enc(id)}`, body: req });
}

export async function deleteDocument(id: string): Promise<void> {
  await queue({ kind: 'deleteDocument', docId: id, method: 'DELETE', url: `/api/documents/${enc(id)}` });
  // Drop offline copies so the document does not reappear from the cache after the DELETE is sent.
  await uncacheDocument(id);
  for (const entry of await cacheEntries<DocumentSummary[]>('list:')) {
    if (Array.isArray(entry.value) && entry.value.some((s) => s.id === id)) {
      await cachePut(
        entry.key,
        entry.value.filter((s) => s.id !== id),
      );
    }
  }
}

export interface NewAudioClip {
  id: string;
  blob: Blob;
  mimeType: string;
  durationMs: number;
  createdAt: string;
  seq?: number;
  diarize?: boolean;
  speakerCount?: number;
  /**
   * Crash-recovery copy of the same audio (RecordingResult.recoveryId). Defaults to the copy the
   * blob came from when it was handed out by the recorder (stop() / recoverRecording()).
   */
  recoveryId?: string | null;
}

/**
 * Queue an audio upload (PUT multipart). The blob is stored in IndexedDB until the upload succeeds.
 * Resolves `persisted: false` when it could only be kept in memory: the recovery copy is then kept
 * (discardRecording() leaves it alone) and deleted by the outbox once the server has the audio.
 */
export async function addAudioClip(docId: string, clip: NewAudioClip): Promise<EnqueueResult> {
  if (clip.blob.size === 0) throw new Error('Posnetek je prazen – nič ni bilo posneto.');
  const fields: CreateClipFields = { createdAt: clip.createdAt };
  if (clip.seq !== undefined) fields.seq = clip.seq;
  if (Number.isFinite(clip.durationMs) && clip.durationMs > 0) fields.durationMs = Math.round(clip.durationMs);
  if (clip.diarize !== undefined) fields.diarize = clip.diarize;
  if (clip.speakerCount !== undefined) fields.speakerCount = clip.speakerCount;
  const recoveryId = clip.recoveryId === undefined ? recordingIdOfBlob(clip.blob) : clip.recoveryId;
  return queue({
    kind: 'createAudioClip',
    docId,
    clipId: clip.id,
    method: 'PUT',
    url: `/api/documents/${enc(docId)}/clips/${enc(clip.id)}`,
    body: fields,
    blob: clip.blob,
    blobMimeType: clip.mimeType || clip.blob.type,
    ...(recoveryId ? { recoveryId } : {}),
  });
}

/** Queue a text-only clip (typed inspection entry or typed note). */
export async function addTextClip(
  docId: string,
  clip: { id: string; text: string; createdAt: string; seq?: number },
): Promise<void> {
  const body: CreateClipFields = { text: clip.text, createdAt: clip.createdAt };
  if (clip.seq !== undefined) body.seq = clip.seq;
  await queue({
    kind: 'createTextClip',
    docId,
    clipId: clip.id,
    method: 'PUT',
    url: `/api/documents/${enc(docId)}/clips/${enc(clip.id)}`,
    body,
  });
}

export async function updateClip(docId: string, clipId: string, req: UpdateClipRequest): Promise<void> {
  await queue({ kind: 'updateClip', docId, clipId, method: 'PATCH', url: `/api/clips/${enc(clipId)}`, body: req });
}

export async function deleteClip(docId: string, clipId: string): Promise<void> {
  await queue({ kind: 'deleteClip', docId, clipId, method: 'DELETE', url: `/api/clips/${enc(clipId)}` });
}

export async function retranscribeClip(docId: string, clipId: string): Promise<void> {
  await queue({
    kind: 'retranscribeClip',
    docId,
    clipId,
    method: 'POST',
    url: `/api/clips/${enc(clipId)}/retranscribe`,
  });
}

export async function updateSegment(docId: string, clipId: string, segmentId: string, req: UpdateSegmentRequest): Promise<void> {
  await queue({
    kind: 'updateSegment',
    docId,
    clipId,
    segmentId,
    method: 'PATCH',
    url: `/api/segments/${enc(segmentId)}`,
    body: req,
  });
}
